import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Archive } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { formatRecordId } from "../lib/utils-format";

// Returns the 409 duplicate payload from an API error, or null.
export const duplicateFromError = (e) => {
  const d = e?.response?.status === 409 ? e.response.data?.detail : null;
  return d && d.code === "duplicate_apn" ? d : null;
};

// Wraps a create call: on a duplicate-APN 409 it opens the warning; "Create Anyway" re-runs with allow=true.
export function useDuplicateGuard() {
  const [dup, setDup] = useState(null);
  const run = async (fn) => {
    try {
      return await fn(false);
    } catch (e) {
      const d = duplicateFromError(e);
      if (!d) throw e;
      setDup({ ...d, retry: () => fn(true) });
      return null;
    }
  };
  return { dup, setDup, run };
}

export function DuplicateApnDialog({ dup, onClose, onCreateAnyway, busy }) {
  const navigate = useNavigate();
  const matches = dup?.matches || [];
  return (
    <Dialog open={!!dup} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="duplicate-apn-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-400" /> Possible duplicate property
          </DialogTitle>
          <DialogDescription>
            APN <span className="font-semibold text-foreground">{dup?.apn}</span> already exists on {matches.length} record{matches.length === 1 ? "" : "s"} (dashes ignored, same ZIP), including archived records.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {matches.map((m) => (
            <button key={`${m.resource}-${m.id}`} type="button" data-testid={`duplicate-match-${m.id}`}
              onClick={() => { onClose(); navigate(`/record/${m.resource}/${m.id}`); }}
              className="w-full text-left rounded-md border border-border px-3 py-2 hover:bg-accent transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{m.name}</span>
                {m.archived && <span className="flex items-center gap-1 text-[10px] uppercase text-amber-400"><Archive className="h-3 w-3" />Archived</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {m.stage}{m.record_no ? ` · ${formatRecordId(m.resource, m.record_no)}` : ""} · APN {m.apn}{m.zip ? ` · ${m.zip}` : ""}{m.status ? ` · ${m.status}` : ""}
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="duplicate-cancel-btn">Cancel</Button>
          <Button onClick={onCreateAnyway} disabled={busy} data-testid="duplicate-create-anyway-btn">Create Anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
